/* eslint-disable @typescript-eslint/naming-convention */
import CONSTANTS, { FIELD_TYPE } from '../constants/Constants';
import {
  IAddressResponse,
  IUPSValidation,
  IAddressSuggestionResponse,
  ITransPaymentResponse,
  IShippingMethodSummary,
  IFindSalonsResponse,
  IGetTransactionKeyResponse,
  ISetTransactionKeyResponse,
  ICompanyBillingDetailsResponse,
  IError,
  IGiftCardResponse,
  IPaymentSession,
  IShippingMethodsResponse,
} from './IApiResponses';
import {
  IAcceptedPrivacyPolicy,
  INewsletterSubscribed,
  IFromAddressMapping,
  GiftCard,
  IFromShippingMethodMapping,
  IFromFindSalonsResponse,
  IFromSetTransactionKeyResponse,
  IFromGetTransactionKeyResponse,
  IFromCompanyBillingDetailsResponse,
  IToCompanyBillingDetailsRequest,
  ISuggestions,
  IFromAddressValidationMapping,
  IToCardMessageMapping,
} from './IDataMappings';
import { ISalon } from '../domain/entities/Salon/Salon';
import { IAddressSchemaProperties } from './JsonSchemaApi';
import dataMappingsModules from './dataMapper';
import { INotificationResponse } from './IApiResponses';
import { IGiftMessageData } from './GiftWrapApi';
import { IAddressAttributes } from '../domain/entities/Address';
import { IDeliveryOption } from '../domain/entities/Transaction/Transaction';

function zipcodeToUpperCase(value: string): string {
  return value && value.toUpperCase();
}

export function formatSuggestions(
  suggestions: IAddressSuggestionResponse[],
): IAddressSuggestionResponse[] {
  return suggestions.map(suggestion => ({
    ...suggestion,
    zipcode: zipcodeToUpperCase(suggestion.postal_code),
  }));
}

const DataMappings = {

  fromAcceptedPrivacyPolicy: {
    response(resp: { accepted_privacy_policy: boolean }): IAcceptedPrivacyPolicy {
      const {
        accepted_privacy_policy: acceptedPrivacyPolicy,
      } = resp;

      return {
        acceptedPrivacyPolicy: !!acceptedPrivacyPolicy,
      };
    },
  },

  fromAddGiftCard: {
    response(resp: IGiftCardResponse): GiftCard {
      const giftCard = resp.trans_payments.find(
        (payment: ITransPaymentResponse) => payment.pp_name === CONSTANTS.GIFT_CARD,
      );

      return {
        amount: giftCard.payment_amount,
        balance: giftCard.balance,
        id: giftCard.trans_payment_id,
        number: giftCard.gc_giftcard_number,
        paymentMethods: resp.payment_init?.[0] || null,
        paymentSession: (resp.payment_init?.[0] as IPaymentSession)?.paymentSession || null,
      };
    },
  },

  fromAddressValidation: {
    response(resp: {
      valid?: boolean,
      errors?: IError[],
    }): IFromAddressValidationMapping {
      return {
        errors: resp.errors,
        valid: !!resp.valid,
      };
    },
  },

  fromCompanyBillingDetailsInfo: {
    response(
      data: ICompanyBillingDetailsResponse,
    ): IFromCompanyBillingDetailsResponse {
      return {
        [FIELD_TYPE.BILL_TO_COMPANY]: !!data.bill_to_company,
        [FIELD_TYPE.COMPANY_ADDRESS]: data.company_address,
        [FIELD_TYPE.COMPANY_NAME]: data.company_name,
        [FIELD_TYPE.COMPANY_TAX_NUMBER]: data.company_tax_number,
      };
    },
  },

  fromDeliveryOptions: {
    response(resp: IShippingMethodsResponse): IDeliveryOption[] {
      return dataMappingsModules.deliveryOptionsMapper.response(
        resp.v2_delivery_options,
      );
    },
  },

  fromFindSalons: {
    response(resp: IFindSalonsResponse): IFromFindSalonsResponse {
      const salons: ISalon[] = [];
      const {
        data: {
          doors: {
            results,
          },
        },
      } = resp;
      Object.keys(results).forEach((id: string) => {
        const {
          ADDRESS: doorAddress,
          CITY: doorCity,
          SALON_ID: salonId,
          DOORNAME: doorName,
          STATE_OR_PROVINCE: doorState,
        } = results[id];
        salons.push({
          doorAddress,
          doorCity,
          doorName,
          doorState,
          salonId,
        });
      });

      return {
        salons,
      };
    },
  },

  fromGetSuggestionAddresses: {
    response(resp: { suggestions: IAddressResponse[] }): ISuggestions {
      return {
        suggestions: resp.suggestions.map(suggestion => dataMappingsModules.addressMapper.response(suggestion)),
      };
    },
  },

  fromGetTransactionKey: {
    response(data: IGetTransactionKeyResponse): IFromGetTransactionKeyResponse {
      return {
        transKey: data.trans_key,
      };
    },
  },

  fromSetTransactionKey: {
    response(data: ISetTransactionKeyResponse): IFromSetTransactionKeyResponse {
      return {
        validatedTrans: data.validated_trans,
      };
    },
  },

  fromShipMethod: {
    response(resp: IShippingMethodSummary): IFromShippingMethodMapping {
      return {
        orderSummary: dataMappingsModules.orderSummaryMapper.response(resp.order_summary),
        transId: resp.trans_id,
      };
    },
  },

  fromSubscription: {
    response(resp: { newsletter_signup: number }): INewsletterSubscribed {
      const { newsletter_signup: newsletterSubscribed } = resp;
      return {
        newsletterSubscribed: !!newsletterSubscribed,
      };
    },
  },

  fromSuggestions: {
    response(resp: IUPSValidation): IUPSValidation {
      return {
        indicators: resp.indicators,
        suggestions: formatSuggestions(resp.suggestions),
      };
    },
  },

  fromWithAddress: {
    response(resp: {
      address: IAddressResponse,
      errors: IError[],
      notifications: INotificationResponse[],
    }): IFromAddressMapping {
      return {
        address: dataMappingsModules.addressMapper.response(resp.address),
        errors: resp.errors,
        notifications: resp.notifications,
      };
    },
  },

  toAddressSchema: {
    response(addressSchema: IAddressSchemaProperties): IAddressSchemaProperties {
      return addressSchema;
    },
  },

  toCardMessage: {
    request(request : IGiftMessageData): IToCardMessageMapping {
      const {
        cardMessageTo,
        cardMessageFrom,
        cardMessage,
        transId,
      } = request;

      return {
        card_message: cardMessage,
        card_message_from: cardMessageFrom,
        card_message_to: cardMessageTo,
        trans_id: transId,
      };
    },
  },

  toCompanyBillingDetailsRequest: {
    request(
      request: IFromCompanyBillingDetailsResponse & { type: string },
    ): IToCompanyBillingDetailsRequest {
      return {
        bill_to_company: {
          ...request,
          bill_to_company: Number(request.bill_to_company),
        },
      };
    },
  },

  toSelectedAddressFields: {
    response(resp: { suggestions: IAddressResponse[] } ): IAddressAttributes {
      const { suggestions } = resp;
      const addressFields = suggestions.length ? suggestions[0] : null;
      return dataMappingsModules.addressMapper.response(addressFields);
    },
  },
};

// this file is way to big, lets gradually move each mapper into its own file
export default { ...DataMappings, ...dataMappingsModules };